/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Stack, TextField } from "@mui/material";
import "./ApplicationDetailsDescriptions.css";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PropTypes from "prop-types";

const calculateEffectiveLanguage = (currentLanguage, submissionData) => {
  if (currentLanguage && currentLanguage !== "") {
    return currentLanguage;
  }
  const sessionLang = sessionStorage.getItem("currentLanguage");
  if (sessionLang && sessionLang !== "") {
    return sessionLang;
  }
  const defaultLang = submissionData?.defaultLanguage || submissionData?.defaultLang;
  if (defaultLang) {
    return defaultLang;
  }
  if (submissionData?.descriptions && !Array.isArray(submissionData.descriptions)) {
    const keys = Object.keys(submissionData.descriptions);
    if (keys.length > 0) {
      return keys[0];
    }
  }
  return null;
};

const applyArrayDescriptions = (submissionData, currentLanguage, setters) => {
  const {
    setShortDesc, setShortDescCharacterCount, setShortDescBool,
    setLongDesc, setLongDecsCharacterCount, setLongDescBool, setWhatsNew,
  } = setters;
  const descriptions = submissionData.descriptions.filter(
    (data) => data.language === currentLanguage
  );
  if (descriptions && descriptions.length > 0 && descriptions[0].shortDescription !== null) {
    setShortDesc(descriptions[0].shortDescription);
    setShortDescCharacterCount(descriptions[0].shortDescription.length);
    setShortDescBool(false);
  } else {
    setShortDesc("");
    setShortDescCharacterCount(0);
  }
  if (descriptions && descriptions.length > 0 && descriptions[0].longDescription !== null) {
    setLongDesc(descriptions[0].longDescription);
    setLongDecsCharacterCount(descriptions[0].longDescription.length);
    setLongDescBool(false);
  } else {
    setLongDesc("");
    setLongDecsCharacterCount(0);
  }
  if (descriptions && descriptions.length > 0 && descriptions[0].whatIsNew !== null) {
    setWhatsNew(descriptions[0].whatIsNew);
  } else {
    setWhatsNew("");
  }
};

const applyObjectDescriptions = (submissionData, effectiveLang, setters) => {
  const {
    setShortDesc, setShortDescCharacterCount, setShortDescBool,
    setLongDesc, setLongDecsCharacterCount, setLongDescBool, setWhatsNew,
  } = setters;
  const descriptions = submissionData.descriptions[effectiveLang];
  if (!descriptions) {
    return;
  }
  if (descriptions.shortText !== null && descriptions.shortText !== undefined) {
    setShortDesc(descriptions.shortText);
    setShortDescCharacterCount(descriptions.shortText.length);
    setShortDescBool(false);
  } else {
    setShortDesc("");
    setShortDescCharacterCount(0);
  }
  if (descriptions.description !== null && descriptions.description !== undefined) {
    setLongDesc(descriptions.description);
    setLongDecsCharacterCount(descriptions.description.length);
    setLongDescBool(false);
  } else {
    setLongDesc("");
    setLongDecsCharacterCount(0);
  }
  if (descriptions.whatIsNew && descriptions.whatIsNew !== null) {
    setWhatsNew(descriptions.whatIsNew);
  } else {
    setWhatsNew("");
  }
};

function ApplicationDetailsDescriptions({ postCallTheNewSubmission }) {
  const appData = useSelector(
    (state) => state.applicationDetails.applicationDetails
  );
  const submissionData = useSelector(
    (state) => state.submissionDetails.submissionDetails
  );

  const currentLanguage = useSelector(
    (state) => state.languageDetails.currentLanguage
  );

  const binary = useSelector((state) => state.binaryDetails.binary);
  const [shortDesc, setShortDesc] = useState("");
  const [longDesc, setLongDesc] = useState("");
  const [whatsNew, setWhatsNew] = useState("");
  const [shortDescCharacterCount, setShortDescCharacterCount] = useState(0);
  const [longDecsCharacterCount, setLongDecsCharacterCount] = useState(0);
  const [shortDescBool, setShortDescBool] = useState(false);
  const [longDescBool, setLongDescBool] = useState(false);
  const [addWhatsNew, setAddWhatsNew] = useState(false);
  const [effectiveLanguage, setEffectiveLanguage] = useState(null);

  useEffect(() => {
    if (!submissionData || Object.keys(submissionData).length === 0) {
      return;
    }

    const effectiveLang = calculateEffectiveLanguage(currentLanguage, submissionData);
    setEffectiveLanguage(effectiveLang);

    const setters = {
      setShortDesc,
      setShortDescCharacterCount,
      setShortDescBool,
      setLongDesc,
      setLongDecsCharacterCount,
      setLongDescBool,
      setWhatsNew,
    };

    if (
      sessionStorage.getItem("customPortal") !== "oem-admin" &&
      submissionData.descriptions.length > 0
    ) {
      applyArrayDescriptions(submissionData, currentLanguage, setters);
    } else if (
      submissionData.descriptions &&
      !Array.isArray(submissionData.descriptions) &&
      Object.keys(submissionData.descriptions).length > 0 &&
      effectiveLang
    ) {
      applyObjectDescriptions(submissionData, effectiveLang, setters);
    }
  }, [currentLanguage, submissionData]);

  useEffect(() => {
    if (binary && Object.keys(binary).length > 0) {
      setAddWhatsNew(true);
    }
  }, [binary]);

  useEffect(() => {
    if (
      appData &&
      Object.keys(appData).length > 0 &&
      appData.submissions.length > 1
    ) {
      if (
        submissionData &&
        Object.keys(submissionData).length > 0 &&
        submissionData.status === "DRAFT"
      ) {
        setAddWhatsNew(true);
      }
    }
  }, [appData, submissionData]);

  const handleChangeTextValue = ($event, index) => {
    if (index === 1) {
      if ($event.target.value === "" || $event.target.value === null) {
        setShortDesc(null);
        setShortDescCharacterCount(0);
        setShortDescBool(true);
        setShortDescCharacterCount(0);
      } else {
        setShortDesc($event.target.value);
        setShortDescCharacterCount($event.target.value.length);
        setShortDescBool(false);
        setShortDescCharacterCount($event.target.value.length);
      }
    } else if (index === 2) {
      if ($event.target.value === "" || $event.target.value === null) {
        setLongDesc(null);
        setLongDecsCharacterCount(0);
        setLongDescBool(true);
        setLongDecsCharacterCount(0);
      } else {
        setLongDesc($event.target.value);
        setLongDecsCharacterCount($event.target.value.length);
        setLongDescBool(false);
        setLongDecsCharacterCount($event.target.value.length);
      }
    }
  };

  const handleWhatsNewValue = ($event) => {
    if ($event.target.value === "") {
      setWhatsNew(null);
    } else {
      setWhatsNew($event.target.value);
    }
  };

  const handleWhatsNewBlur = ($event) => {
    let newDescriptions = structuredClone(submissionData.descriptions);
    newDescriptions.map((data) => {
      if (data.language === currentLanguage) {
        data.whatIsNew =
          $event.target.value === "" ? null : $event.target.value;
      }
    });
    let newSubmissionData = {
      ...submissionData,
      descriptions: newDescriptions,
    };
    delete newSubmissionData["legalDocuments"];
    postCallTheNewSubmission(newSubmissionData);
  };

  const handleSubmit = ($event, index) => {
    let newDescriptions = structuredClone(submissionData.descriptions);
    if (index === 1) {
      setShortDesc($event.target.value);
      newDescriptions.map((data) => {
        if (data.language === currentLanguage) {
          data.shortDescription =
            $event.target.value === "" ? null : $event.target.value;
        }
      });
      let newSubmissionData = {
        ...submissionData,
        descriptions: newDescriptions,
      };
      delete newSubmissionData["legalDocuments"];
      postCallTheNewSubmission(newSubmissionData);
    } else if (index === 2) {
      newDescriptions.map((data) => {
        if (data.language === currentLanguage) {
          data.longDescription =
            $event.target.value === "" ? null : $event.target.value;
        }
      });
      let newSubmissionData = {
        ...submissionData,
        descriptions: newDescriptions,
      };
      delete newSubmissionData["legalDocuments"];
      postCallTheNewSubmission(newSubmissionData);
    }
  };
  const renderDescriptionContent = () => {
    if (
      submissionData &&
      Object.keys(submissionData).length > 0 &&
      submissionData.status === "DRAFT"
    ) {
      return (
        <div style={{ width: "100%" }}>
          <Stack direction="column" spacing={2}>
            
            {addWhatsNew && (
              <>
                <div className="new-change">
                  <p className="input-title-description">
                    New Changes & Fixes <span className="required-desc">*</span>
                  </p>
                  <p className="input-subtitle-description">
                    Describe any recent bug fixes or changes implemented in this
                    update.
                  </p>
                </div>
                <div className="whats-new-container">
                  <TextField
                    sx={{
                      "&.MuiFormControl-root": { width: "100%" },
                      "& .MuiInputBase-root": {
                        height: "fit-content !important",
                        alignItems: "start",
                      },
                      "& textarea": {
                        height: "164px !important",
                        minHeight: "164px !important",
                        resize: "none",
                        overflow: "auto",
                      },
                    }}
                    multiline
                    rows={10}
                    maxRows={30}
                    className="input-fields whats-new-textarea"
                    id="whats-new"
                    type="text"
                    variant="outlined"
                    defaultValue={whatsNew}
                    value={whatsNew}
                    onChange={($event) => handleWhatsNewValue($event)}
                    onBlur={($event) => handleWhatsNewBlur($event)}
                    required
                  />
                  <div
                    className="whats-new-resize-handle"
                    title="Indicator"
                    aria-hidden="true"
                  >
                    <ArrowDropDownIcon fontSize="small" />
                  </div>
                </div>
              </>
            )}
            <p className="input-text-description">
              Short Description <span className="required-desc">*</span>
            </p>
            <TextField
              sx={{
                "&.MuiFormControl-root": {
                  width: "100%",
                },
                "&.MuiFormHelperText-root": {
                  display: "flex",
                  justifyContent: "flex-end",
                },
              }}
              slotProps={{
                htmlInput: { maxLength: 80 },
                formHelperText: {
                  style: {
                    display: "flex",
                    justifyContent: "flex-end",
                    marginRight: "0px",
                  },
                },
              }}
              className="input-fields"
              id="outlined-basic"
              type="text"
              variant="outlined"
              defaultValue={shortDesc}
              value={shortDesc}
              onChange={($event) => handleChangeTextValue($event, 1)}
              helperText={`${shortDescCharacterCount}/80`}
              onBlur={($event) => handleSubmit($event, 1)}
              required
            />
            {shortDescBool && (
              <p className="shortDescRequired">
                *Please fill the short description Value
              </p>
            )}

            <p className="input-text-description">
              Long Description <span className="required-desc">*</span>
            </p>
            <TextField
              sx={{
                "&.MuiFormControl-root": {
                  width: "100%",
                },
                "&.MuiInputBase-root": {
                  height: "fit-content !important",
                },
                display: "flex",
                flexGrow: 1,
              }}
              slotProps={{
                input: {
                  sx: {
                    height: "fit-content !important",
                    alignItems: "start",
                  },
                },
                htmlInput: { maxLength: 4000 },
                formHelperText: {
                  style: {
                    display: "flex",
                    justifyContent: "flex-end",
                    marginRight: "0px",
                  },
                },
              }}
              rows={10}
              maxRows={15}
              size="large"
              className="input-fields"
              id="outlined-basic"
              type="text"
              variant="outlined"
              defaultValue={longDesc}
              value={longDesc}
              onChange={($event) => handleChangeTextValue($event, 2)}
              onBlur={($event) => handleSubmit($event, 2)}
              helperText={`${longDecsCharacterCount}/4000`}
              multiline
              required
            />
            {longDescBool && (
              <p className="longDescRequired">
                *Please fill the long description value
              </p>
            )}

            <button
              style={{ visibility: "hidden" }}
              type="sumit"
              form="accept"
            />
          </Stack>
        </div>
      );
    }
    if (
      submissionData &&
      Object.keys(submissionData).length > 0 &&
      sessionStorage.getItem("customPortal") !== "oem-admin"
    ) {
      return (
        <div className="descriptions-border">
          <Stack direction="column" spacing={"8px"}>
            {whatsNew !== "" && whatsNew !== null && (
              <div className="service-link-not-draft">
                <p className="input-text-description">What's New</p>
                <p className="desc">{whatsNew}</p>
              </div>
            )}

            <div className="service-link-not-draft">
              <p className="input-text-description">Application Name</p>
              <p className="desc">
                {submissionData.descriptions[0].applicationName}
              </p>
            </div>
            <div className="service-link-not-draft">
              <p className="input-text-description">Short Description</p>
              <p className="desc">{shortDesc}</p>
            </div>
            <div className="service-link-not-draft">
              <p className="input-text-description">Long Description</p>
              <p className="desc">{longDesc}</p>
            </div>
          </Stack>
        </div>
      );
    }
    return (
      <div className="descriptions-border">
        <Stack direction="column" spacing={"8px"}>
          {whatsNew !== "" && whatsNew !== null && (
            <div>
              <p className="input-text-description">What's New</p>
              <p className="desc">{whatsNew}</p>
            </div>
          )}
          <div>
            <p className="input-text-description">Application Name</p>
            <p className="desc">
              {(() => {
                const lang = effectiveLanguage || currentLanguage || submissionData?.defaultLang || Object.keys(submissionData?.descriptions || {})[0];
                return lang && submissionData?.descriptions?.[lang]?.title;
              })()}
            </p>
          </div>
          <div>
            <p className="input-text-description">Short Description</p>
            <p className="desc">
              {(() => {
                const lang = effectiveLanguage || currentLanguage || submissionData?.defaultLang || Object.keys(submissionData?.descriptions || {})[0];
                return shortDesc || (lang && submissionData?.descriptions?.[lang]?.shortText);
              })()}
            </p>
          </div>
          <div>
            <p className="input-text-description">Long Description</p>
            <p className="desc">
              {(() => {
                const lang = effectiveLanguage || currentLanguage || submissionData?.defaultLang || Object.keys(submissionData?.descriptions || {})[0];
                return longDesc || (lang && submissionData?.descriptions?.[lang]?.description);
              })()}
            </p>
          </div>
        </Stack>
      </div>
    );
  };

  return (
    <div id="description" style={{ width: "100%", marginTop: "8px" }}>
      {renderDescriptionContent()}
    </div>
  );
}

ApplicationDetailsDescriptions.propTypes = {
  postCallTheNewSubmission: PropTypes.func.isRequired,
};

export default ApplicationDetailsDescriptions;
